import React, { forwardRef, useMemo } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import { endpoints } from "Core/defaultValues";

//Utils
import { getFormattedDateByTimeZone } from "Core/data/Helpers";
// Styles
import "./video.scss";

// Components
import { VideoPlayer, Image360, LinearProgress } from "Components";
import { Icon, IconButton } from "@mui/material";
import { ModalImg } from "./components/modalImg";

import {
    DEVICE_MODEL_ENUM,
    MODE_ENUM,
    MANUFACTURER_ENUM
} from "./constants";

import { useIntl } from "react-intl";
import { useVideo } from "./useVideo";

export const Thumbnail = ({
    id,
    deviceModel,
    dewrapImage,
    thumbnailUrl,
    isUpsidedown,
}) => {
    return (
        <div style={{ width: "100%", height: "100%" }}>
            {deviceModel === "360" && dewrapImage ? (
                <Image360
                    imageUrl={thumbnailUrl}
                    id={id}
                    isUpsidedown={isUpsidedown}
                />
            ) : (
                thumbnailUrl && (
                    <img src={thumbnailUrl} style={{ width: "100%", height: "100%",  objectFit: "contain" }} />
                )
            )}
        </div>
    );
};

export const Playback = ({
    id,
    isUpsidedown,
    videoPlayerRef,
    options,
    response,
    playingVideo,
    thumbnailUrl,
    url,
    deviceModel,
    dewrapImage,
    timeZone
}) => {
    const { messages } = useIntl();

    return (
        <div style={{ width: "100%", height: "100%", position: "relative" }}>
            {/*
                url && playingVideo && (
                    <VideoPlayer
                        isUpsidedown={isUpsidedown}
                        ref={videoPlayerRef}
                        {...options}
                    />
                )
            */}

            {
                url && playingVideo && (
                    <video
                        controls
                        style={{
                            width: "96%",
                            marginLeft: "2%",
                            height: "100%",
                            position: "relative",
                        }}
                    >
                        <source
                            src={url}
                            type="video/mp4"
                        />
                    </video>
                )
            }

            {
                thumbnailUrl && !playingVideo && (
                    <Thumbnail
                        id={id}
                        deviceModel={deviceModel}
                        dewrapImage={dewrapImage}
                        thumbnailUrl={thumbnailUrl}
                        isUpsidedown={isUpsidedown}
                    />
                )
            }

            {
                response?.time && (
                    <div style={{ position: "absolute", bottom: 8, right: 8 }}>
                        <span style={{ color: "white", fontSize: 14 }}>
                            {getFormattedDateByTimeZone(
                                response?.time,
                                timeZone,
                                messages,
                                "HH:mm:ss"
                            )}
                        </span>
                    </div>
                )
            }
        </div>
    );
};

export const PlayButton = ({
    thumbnailUrl,
    onClick,
    setPlayingVideo,
    setShowPlayButton,
    disabled,
    error,
    mode
}) => {
    return (
        <div className="video-play-button">
            <IconButton
                aria-label="play"
                style={{ color: thumbnailUrl ? "black" : "white" }}
                disabled={disabled}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onClick && onClick();
                    if (mode === MODE_ENUM.playback) {
                        setPlayingVideo(true);
                        setShowPlayButton(false);
                    }
                }}
                size="large"
            >
                <Icon
                    className={"icon-video-play-replay"}
                    style={{
                        fontSize: 50,
                        color: disabled ? "rgba(255, 255, 255, 0.3)" : "white",
                    }}
                >
                    {error ? "replay" : "play_arrow"}
                </Icon>
            </IconButton>
        </div>
    );
};

export const RetryButton = ({
    onClick,
    disabled
}) => {
    return (
        <div className="video-play-button">
            <IconButton
                aria-label="play"
                style={{ color: "white" }}
                disabled={disabled}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onClick && onClick();
                }}
                size="large"
            >
                <Icon
                    className={"icon-video-play-replay"}
                    style={{
                        fontSize: 50,
                        color: disabled ? "rgba(255, 255, 255, 0.3)" : "white",
                    }}
                >
                    replay
                </Icon>
            </IconButton>
        </div>
    );
};

export const SearchButton = ({ thumbnailUrl, disabled, setOpenImg }) => {
    return (
        <div className="video-play-button">
            <IconButton
                aria-label="search"
                style={{ color: thumbnailUrl ? "black" : "white" }}
                disabled={disabled}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (thumbnailUrl) {
                        setOpenImg(true);
                    }
                }}
                size="large"
            >
                <Icon
                    className={"icon-video-play-replay"}
                    style={{
                        fontSize: 50,
                        color: disabled ? "rgba(255, 255, 255, 0.3)" : "white",
                    }}
                >
                    search
                </Icon>
            </IconButton>
        </div>
    );
};

//This component uses Video.js library, for more information please visit the Video.js documentation
//and search about the player options
const VideoComponent = forwardRef((props, ref) => {
    const {
        disabled,
        deviceModel,
        dewrapImage,
        id,
        isUpsidedown,
        videoPlayerRef,
        options,
        footageResponse,
        footageError,
        timeZone,
        thumbnailUrl,
        retry,
        setPlayingVideo,
        setShowPlayButton,
        setOpenImg,
        playingVideo,
        showPlayButton,
        showLoading,
        message,
        openImg,
        loading,
        mode,
        error,
        url,
    } = useVideo(ref, props);

    return (
        <div
            className="video-container"
            style={{ backgroundColor: disabled ? "white" : "black" }}
        >
            <div className="video-actions-container">
                {
                    (loading || showLoading) && (
                        <LinearProgress />
                    )
                }

                <div className="video-message">{message}</div>

                {
                    showPlayButton &&
                    !loading &&
                    !showLoading &&
                    (mode === MODE_ENUM.playback || mode === MODE_ENUM.picture || mode === MODE_ENUM.snapshot) &&
                    (
                        <PlayButton
                            thumbnailUrl={thumbnailUrl}
                            onClick={retry}
                            setPlayingVideo={setPlayingVideo}
                            setShowPlayButton={setShowPlayButton}
                            disabled={disabled}
                            response={footageResponse}
                            error={footageError}
                            mode={mode}
                        />
                    )
                }

                {
                    (mode === MODE_ENUM.picture || mode === MODE_ENUM.snapshot) &&
                    !loading &&
                    !showLoading &&
                    thumbnailUrl &&
                    <SearchButton
                        thumbnailUrl={thumbnailUrl}
                        disabled={disabled}
                        setOpenImg={setOpenImg}
                    />
                }
            </div>

            {
                (mode === MODE_ENUM.picture || mode === MODE_ENUM.snapshot) && (
                    <Thumbnail
                        id={id}
                        deviceModel={deviceModel}
                        dewrapImage={dewrapImage}
                        thumbnailUrl={thumbnailUrl}
                        isUpsidedown={isUpsidedown}
                    />
                )
            }

            {
                mode === MODE_ENUM.playback && deviceModel === DEVICE_MODEL_ENUM.device4k && (
                    <Playback
                        id={id}
                        deviceModel={deviceModel}
                        dewrapImage={dewrapImage}
                        isUpsidedown={isUpsidedown}
                        videoPlayerRef={videoPlayerRef}
                        options={options}
                        thumbnailUrl={thumbnailUrl}
                        url={url}
                        response={footageResponse}
                        timeZone={timeZone}
                        playingVideo={playingVideo}
                    />
                )
            }

            <ModalImg
                setOpenImg={setOpenImg}
                openImg={openImg}
                img={thumbnailUrl || url}
            />
        </div>
    );
});

export default VideoComponent;

VideoComponent.propTypes = {
    readingId: PropTypes.string,
    deviceId: PropTypes.string,
    autoplay: PropTypes.bool, //If is true the video will be played automatically
    mode: PropTypes.string.isRequired, //Depends of the playing type, the url VidFleet API changes
    mode: PropTypes.oneOf(["streaming", "playback", "picture", "clip-streaming", "snapshot", "live-photo"]),
    deviceModel: PropTypes.string.isRequired, //4k|360
    deviceModel: PropTypes.oneOf(["4K", "4k", "360"]),
    thumbnailUrl: PropTypes.string,
    url: PropTypes.string,
    disabled: PropTypes.bool, //If is true the user can not play the video
};
